
import { useState, useEffect } from "react";

interface Testimonial {
  quote: string;
  name: string;
  title: string;
  company: string;
}

const testimonials: Testimonial[] = [
  {
    quote: "Bizlytics.io transformed how we analyze our business data. The CEO dashboard gives me instant insights that used to take days to compile.",
    name: "Sarah Mitchell",
    title: "CEO",
    company: "TechGrowth Solutions"
  },
  {
    quote: "The customizable dashboards and AI-powered insights have been a game-changer for our marketing team. We're making smarter decisions faster.",
    name: "Michael Rodriguez",
    title: "CMO",
    company: "Innovate Partners"
  },
  {
    quote: "As CFO, I need accurate financial data at my fingertips. Bizlytics.io integrates perfectly with our systems and presents the data beautifully.",
    name: "Jennifer Wilson",
    title: "CFO",
    company: "Global Retail Group"
  }
];

export function TestimonialSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    
    const section = document.getElementById("testimonials");
    if (section) {
      observer.observe(section);
    }
    
    // Auto-rotate testimonials
    const intervalId = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    
    return () => {
      if (section) {
        observer.unobserve(section);
      }
      clearInterval(intervalId);
    };
  }, []);

  return (
    <section id="testimonials" className="py-20 bg-white dark:bg-slate-950 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            What Our Customers Say
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            See how Bizlytics.io is helping businesses transform their data into actionable insights.
          </p>
        </div>

        <div className={`max-w-4xl mx-auto transition-all duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="relative">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`transition-all duration-700 absolute top-0 left-0 w-full ${
                  activeIndex === index
                    ? "opacity-100 translate-x-0 z-10"
                    : activeIndex > index
                    ? "opacity-0 -translate-x-full z-0" 
                    : "opacity-0 translate-x-full z-0"
                }`}
              >
                <div className="glass-card p-8 md:p-10 text-center">
                  <div className="mb-6">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <svg
                        key={i}
                        className="inline-block h-5 w-5 text-yellow-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <blockquote className="text-xl md:text-2xl font-medium mb-8">
                    "{testimonial.quote}"
                  </blockquote>
                  <div className="flex flex-col items-center">
                    <div className="h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700 mb-4 flex items-center justify-center text-gray-600 dark:text-gray-400">
                      {testimonial.name.charAt(0)}
                    </div>
                    <h4 className="font-bold">{testimonial.name}</h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      {testimonial.title}, {testimonial.company}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Current testimonial for spacing (since the others are absolute positioned) */}
            <div className="invisible">
              <div className="glass-card p-8 md:p-10 text-center">
                <div className="mb-6">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <svg
                      key={i}
                      className="inline-block h-5 w-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <blockquote className="text-xl md:text-2xl font-medium mb-8">
                  "{testimonials[activeIndex].quote}"
                </blockquote>
                <div className="flex flex-col items-center">
                  <div className="h-12 w-12 rounded-full bg-gray-200 mb-4"></div>
                  <h4 className="font-bold">{testimonials[activeIndex].name}</h4>
                  <p className="text-gray-600">
                    {testimonials[activeIndex].title}, {testimonials[activeIndex].company}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`h-2 w-2 rounded-full transition-all duration-300 ${
                  activeIndex === index ? "w-6 bg-primary" : "bg-gray-300 dark:bg-gray-700"
                }`}
                aria-label={`View testimonial ${index + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
